/**
 * The consultant's review inbox (RESTORE_PLAN R1.6, from the pre-November QueueItemsCard):
 * Contact Client opens the in-app Message Center with the reply drafted, Add to To-Do saves a
 * to-do tied to the item, and the status buttons save. Server rules:
 * supabase/functions/server/queueInboxCore.ts.
 */
import { projectId } from './supabase/info';

const BASE = `https://${projectId}.supabase.co/functions/v1/make-server-e90c59cd`;

export interface InboxItem {
  id: string;
  type: 'request' | 'change_order' | 'approval' | 'question' | 'issue';
  companyId: string;
  companyName: string;
  title: string;
  description: string;
  priority: 'low' | 'medium' | 'high' | 'urgent';
  status: 'waiting_consultant' | 'waiting_client' | 'in_progress' | 'resolved';
  createdByName: string;
  createdAt: string;
  dueDate?: string;
}

const TYPE_WORDS: Record<InboxItem['type'], string> = {
  request: 'request',
  change_order: 'change order',
  approval: 'approval',
  question: 'question',
  issue: 'issue',
};

/** The reply the Message Center opens with (the old card's wording, the type in words). */
export function contactDraft(item: Pick<InboxItem, 'title' | 'type' | 'createdByName'>): { subject: string; content: string } {
  const who = item.createdByName?.trim() || 'there';
  return {
    subject: `Re: ${item.title}`,
    content: `Hi ${who},\n\nI'm following up on your ${TYPE_WORDS[item.type] || 'request'}: "${item.title}"\n\n`,
  };
}

/** The to-do an inbox item becomes: its title and client, its due day, a note, and a link back to it. */
export function todoFromInboxItem(item: InboxItem) {
  return {
    text: `${item.title} (${item.companyName})`,
    ...(item.dueDate ? { dueDate: item.dueDate.slice(0, 10) } : {}),
    ...(item.description ? { note: item.description } : {}),
    source: { type: 'queue_item', id: item.id, companyId: item.companyId, companyName: item.companyName, priority: item.priority },
  };
}

function headers(accessToken: string) {
  return { Authorization: `Bearer ${accessToken}`, 'Content-Type': 'application/json' };
}

async function failure(res: Response, fallback: string): Promise<Error> {
  const err = await res.json().catch(() => ({}));
  return new Error(err.error || `${fallback} (${res.status})`);
}

/** Saves the to-do on the caller's own list; `existing` when the item was already on it. */
export async function addInboxItemToTodos(item: InboxItem, userId: string, accessToken: string): Promise<{ existing: boolean }> {
  const res = await fetch(`${BASE}/todos/${encodeURIComponent(userId)}`, {
    method: 'POST',
    headers: headers(accessToken),
    body: JSON.stringify(todoFromInboxItem(item)),
  });
  if (!res.ok) throw await failure(res, 'Could not add the to-do');
  const data = await res.json().catch(() => ({}));
  return { existing: !!data.existing };
}

export async function setInboxItemStatus(itemId: string, status: InboxItem['status'], accessToken: string): Promise<InboxItem> {
  const res = await fetch(`${BASE}/consultant/queue-items/${encodeURIComponent(itemId)}/status`, {
    method: 'PUT',
    headers: headers(accessToken),
    body: JSON.stringify({ status }),
  });
  if (!res.ok) throw await failure(res, 'Could not update the item');
  const data = await res.json();
  return data.item;
}

/** The inbox: items waiting on the consultant or in progress, for the caller's own firm. */
export async function loadInboxItems(accessToken: string): Promise<InboxItem[]> {
  const res = await fetch(`${BASE}/consultant/queue-items?status=open`, { headers: { Authorization: `Bearer ${accessToken}` } });
  if (!res.ok) throw await failure(res, 'Could not load the review queue');
  const data = await res.json();
  return data.items || [];
}
